import { useMemo, useState, useCallback } from 'react';
import { buildPreview } from '../engine/buildPreview';

// Phase 2.5 draft state: an archetype + a few editable customizations
// (name, plus a hook for future: ability scores, skill picks).
//
// Returns:
//   - draft       : { archetype, customizations }
//   - preview     : engine output (or null when archetype is missing)
//   - setName     : (string) => void
//   - setAbility  : (ability, score) => void  (Phase 2.6+)
//   - reset       : () => void

export const useCharacterDraft = (archetype) => {
  const [name, setName] = useState('');
  const [customAbilities, setCustomAbilities] = useState(null);

  const customizations = useMemo(
    () => ({
      name,
      abilities: customAbilities || archetype?.picks?.abilities,
    }),
    [name, customAbilities, archetype],
  );

  const preview = useMemo(
    () => (archetype ? buildPreview(archetype, customizations) : null),
    [archetype, customizations],
  );

  const setAbility = useCallback(
    (ability, score) => {
      setCustomAbilities((prev) => ({
        ...(prev || archetype?.picks?.abilities || {}),
        [ability]: score,
      }));
    },
    [archetype],
  );

  const reset = useCallback(() => {
    setName('');
    setCustomAbilities(null);
  }, []);

  return {
    draft: { archetype, customizations },
    preview,
    name,
    setName,
    setAbility,
    reset,
  };
};
